import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCustomerAuth } from './contexts/CustomerAuthContext';
import {
  LayoutDashboard,
  ShoppingCart,
  Heart,
  MapPin,
  User,
  LogOut,
  Bell,
  RotateCcw,
  Tag,
  ChevronDown,
  Package,
  Menu,
  X,
  BarChart3,
  Download,
  MoreHorizontal,
  ChevronRight
} from 'lucide-react';
import './CustomerShell.css';

const Icon = ({ name, size = 20 }) => {
  const icons = {
    dashboard: <LayoutDashboard size={size} />,
    orders: <Package size={size} />,
    cart: <ShoppingCart size={size} />,
    wishlist: <Heart size={size} />,
    addresses: <MapPin size={size} />,
    profile: <User size={size} />,
    logout: <LogOut size={size} />,
    bell: <Bell size={size} />,
    refunds: <RotateCcw size={size} />,
    promotions: <Tag size={size} />,
    menu: <Menu size={size} />,
    close: <X size={size} />,
    analytics: <BarChart3 size={size} />,
    download: <Download size={size} />,
    more: <MoreHorizontal size={size} />
  };
  return icons[name] || null;
};

const NavLink = ({ to, icon, children, active, badge, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    className={`cs-nav__link ${active ? 'is-active' : ''}`}
  >
    <span className="cs-nav__icon"><Icon name={icon} size={18} /></span>
    <span className="cs-nav__label">{children}</span>
    {badge > 0 && <span className="cs-nav__badge">{badge > 99 ? '99+' : badge}</span>}
  </Link>
);

const BottomNavItem = ({ to, icon, label, active, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    className={`cs-bottom__item ${active ? 'is-active' : ''}`}
    aria-label={label}
  >
    <Icon name={icon} size={20} />
    <span className="cs-bottom__label">{label}</span>
  </Link>
);

export default function CustomerShell({ children }) {
  const { customer, isAuthenticated, logout, refreshCustomerData } = useCustomerAuth();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showMoreSheet, setShowMoreSheet] = useState(false);

  const isActive = (path) => location.pathname === path;
  const startsWith = (prefix) => location.pathname === prefix || location.pathname.startsWith(prefix + '/');

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!customer) return;
      try {
        const response = await fetch('/api/customer/notifications', {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        if (response.ok) {
          const data = await response.json();
          setUnreadCount(data.notifications?.filter(n => !n.isRead).length || 0);
        }
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id]);

  // Refresh customer data once on mount
  useEffect(() => {
    if (refreshCustomerData) refreshCustomerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close menus on route change
  useEffect(() => {
    setShowProfileMenu(false);
    setShowMoreSheet(false);
  }, [location.pathname]);

  // Click outside profile menu
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showProfileMenu && !e.target.closest('.cs-profile')) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showProfileMenu]);

  return (
    <div className="cs">
      {/* Desktop sidebar */}
      <aside className="cs-sidebar">
        <div className="cs-sidebar__brand">
          <span className="cs-sidebar__logo">R</span>
          <span className="cs-sidebar__brand-text">My Account</span>
        </div>

        <nav className="cs-nav">
          <NavLink to="/dashboard" icon="dashboard" active={isActive('/dashboard')}>Dashboard</NavLink>
          <NavLink to="/dashboard/orders" icon="orders" active={startsWith('/dashboard/orders')}>Orders</NavLink>
          <NavLink to="/dashboard/downloads" icon="download" active={isActive('/dashboard/downloads')}>Downloads</NavLink>
          <NavLink to="/dashboard/wishlist" icon="wishlist" active={isActive('/dashboard/wishlist')}>Wishlist</NavLink>
          <NavLink to="/dashboard/promotions" icon="promotions" active={isActive('/dashboard/promotions')}>Promotions</NavLink>

          <div className="cs-nav__sep" />

          <NavLink to="/dashboard/profile" icon="profile" active={isActive('/dashboard/profile')}>Profile</NavLink>
          <NavLink to="/dashboard/addresses" icon="addresses" active={isActive('/dashboard/addresses')}>Addresses</NavLink>
          <NavLink to="/dashboard/notifications" icon="bell" active={isActive('/dashboard/notifications')} badge={unreadCount}>Notifications</NavLink>
          <NavLink to="/dashboard/analytics" icon="analytics" active={isActive('/dashboard/analytics')}>Insights</NavLink>
          <NavLink to="/dashboard/refunds" icon="refunds" active={isActive('/dashboard/refunds')}>Refunds</NavLink>
        </nav>

        <button onClick={logout} className="cs-sidebar__logout">
          <Icon name="logout" size={16} />
          <span>Log out</span>
        </button>
      </aside>

      <main className="cs-main">
        <header className="cs-topbar">
          <div className="cs-topbar__title-wrap">
            <h1 className="cs-topbar__title">My Account</h1>
          </div>

          <div className="cs-topbar__actions">
            <Link to="/dashboard/notifications" className="cs-topbar__icon-btn" aria-label="Notifications">
              <Bell size={18} />
              {unreadCount > 0 && <span className="cs-topbar__icon-dot" aria-hidden />}
            </Link>

            <div className="cs-profile">
              <button
                type="button"
                className="cs-profile__trigger"
                onClick={(e) => { e.stopPropagation(); setShowProfileMenu(!showProfileMenu); }}
                aria-label="Profile menu"
              >
                {customer?.profileImage ? (
                  <img className="cs-profile__avatar" src={customer.profileImage} alt={customer?.firstName} />
                ) : (
                  <span className="cs-profile__avatar cs-profile__avatar--placeholder">
                    {customer?.firstName?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                )}
                <span className="cs-profile__name">{customer?.firstName || 'Account'}</span>
                <ChevronDown size={14} className={`cs-profile__chev ${showProfileMenu ? 'is-open' : ''}`} />
              </button>

              {showProfileMenu && (
                <div className="cs-profile__menu" role="menu">
                  <div className="cs-profile__menu-head">
                    <div className="cs-profile__menu-name">{customer?.firstName} {customer?.lastName}</div>
                    <div className="cs-profile__menu-email">{customer?.email}</div>
                  </div>
                  <Link to="/dashboard/profile" className="cs-profile__menu-item">
                    <Icon name="profile" size={16} /><span>Profile settings</span>
                  </Link>
                  <Link to="/dashboard/orders" className="cs-profile__menu-item">
                    <Icon name="orders" size={16} /><span>My orders</span>
                  </Link>
                  <Link to="/dashboard/addresses" className="cs-profile__menu-item">
                    <Icon name="addresses" size={16} /><span>Addresses</span>
                  </Link>
                  <div className="cs-profile__menu-sep" />
                  <button type="button" className="cs-profile__menu-item cs-profile__menu-item--danger" onClick={logout}>
                    <Icon name="logout" size={16} /><span>Log out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="cs-content">
          {children}
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="cs-bottom" aria-label="Primary">
        <BottomNavItem to="/dashboard" icon="dashboard" label="Home" active={isActive('/dashboard')} />
        <BottomNavItem to="/dashboard/orders" icon="orders" label="Orders" active={startsWith('/dashboard/orders')} />
        <BottomNavItem to="/dashboard/wishlist" icon="wishlist" label="Wishlist" active={isActive('/dashboard/wishlist')} />
        <BottomNavItem to="/dashboard/profile" icon="profile" label="Profile" active={isActive('/dashboard/profile')} />
        <button
          type="button"
          className={`cs-bottom__item ${showMoreSheet ? 'is-active' : ''}`}
          onClick={() => setShowMoreSheet(!showMoreSheet)}
          aria-label="More"
        >
          <MoreHorizontal size={20} />
          <span className="cs-bottom__label">More</span>
        </button>
      </nav>

      {/* Mobile "More" sheet */}
      {showMoreSheet && (
        <>
          <div className="cs-sheet__scrim" onClick={() => setShowMoreSheet(false)} />
          <div className="cs-sheet" role="dialog" aria-label="More menu">
            <div className="cs-sheet__handle" />
            <header className="cs-sheet__head">
              {customer?.profileImage ? (
                <img className="cs-sheet__avatar" src={customer.profileImage} alt={customer?.firstName} />
              ) : (
                <span className="cs-sheet__avatar cs-sheet__avatar--placeholder">
                  {customer?.firstName?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              )}
              <div className="cs-sheet__head-text">
                <div className="cs-sheet__name">
                  {customer?.firstName || customer?.lastName
                    ? `${customer?.firstName || ''} ${customer?.lastName || ''}`.trim()
                    : 'Account'}
                </div>
                {customer?.email && <div className="cs-sheet__email">{customer.email}</div>}
              </div>
              <button
                type="button"
                onClick={() => setShowMoreSheet(false)}
                className="cs-sheet__close"
                aria-label="Close"
              >
                <X size={16} aria-hidden />
              </button>
            </header>

            <nav className="cs-sheet__list" aria-label="More navigation">
              <Link to="/dashboard/profile" className="cs-sheet__row">
                <span className="cs-sheet__row-icon"><Icon name="profile" size={16} /></span>
                <span className="cs-sheet__row-label">Profile</span>
                <ChevronRight size={14} className="cs-sheet__row-chev" aria-hidden />
              </Link>
              <Link to="/dashboard/downloads" className="cs-sheet__row">
                <span className="cs-sheet__row-icon"><Icon name="download" size={16} /></span>
                <span className="cs-sheet__row-label">Downloads</span>
                <ChevronRight size={14} className="cs-sheet__row-chev" aria-hidden />
              </Link>
              <Link to="/dashboard/promotions" className="cs-sheet__row">
                <span className="cs-sheet__row-icon"><Icon name="promotions" size={16} /></span>
                <span className="cs-sheet__row-label">Promotions</span>
                <ChevronRight size={14} className="cs-sheet__row-chev" aria-hidden />
              </Link>
              <Link to="/dashboard/addresses" className="cs-sheet__row">
                <span className="cs-sheet__row-icon"><Icon name="addresses" size={16} /></span>
                <span className="cs-sheet__row-label">Addresses</span>
                <ChevronRight size={14} className="cs-sheet__row-chev" aria-hidden />
              </Link>
              <Link to="/dashboard/notifications" className="cs-sheet__row">
                <span className="cs-sheet__row-icon">
                  <Icon name="bell" size={16} />
                  {unreadCount > 0 && <span className="cs-sheet__row-dot" aria-hidden />}
                </span>
                <span className="cs-sheet__row-label">Notifications</span>
                {unreadCount > 0 && (
                  <span className="cs-sheet__row-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
                <ChevronRight size={14} className="cs-sheet__row-chev" aria-hidden />
              </Link>
              <Link to="/dashboard/analytics" className="cs-sheet__row">
                <span className="cs-sheet__row-icon"><Icon name="analytics" size={16} /></span>
                <span className="cs-sheet__row-label">Insights</span>
                <ChevronRight size={14} className="cs-sheet__row-chev" aria-hidden />
              </Link>
              <Link to="/dashboard/refunds" className="cs-sheet__row">
                <span className="cs-sheet__row-icon"><Icon name="refunds" size={16} /></span>
                <span className="cs-sheet__row-label">Refunds</span>
                <ChevronRight size={14} className="cs-sheet__row-chev" aria-hidden />
              </Link>
            </nav>

            <button type="button" onClick={logout} className="cs-sheet__logout">
              <Icon name="logout" size={16} />
              <span>Log out</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
